/**
 * E2E backup of the chat's local state (identity + ratchets + conversations +
 * messages), encrypted with a passphrase that ONLY the user knows.
 *
 * Zero-knowledge: the resulting blob is AES-256-GCM with a key derived by
 * PBKDF2-SHA256 from the passphrase; it can be stored anywhere (disk, someone else's cloud)
 * without revealing anything. It is never uploaded automatically: exporting/importing is an
 * explicit user action. A wrong passphrase or a tampered blob
 * fail GCM authentication and are rejected.
 */

import { base64ToBytes, bytesToBase64, utf8Decode, utf8Encode } from "@susurro/protocol";

import { decode, encode } from "./serialize";
import type { PersistedState } from "./types";

const BACKUP_KIND = "susurro-backup";
const PBKDF2_ITERATIONS = 250_000;

/** Shape of the exported blob (everything binary in base64). */
interface BackupBlob {
  readonly v: 1;
  readonly kind: typeof BACKUP_KIND;
  readonly kdf: "PBKDF2-SHA256";
  readonly iter: number;
  readonly salt: string;
  readonly iv: string;
  readonly ct: string;
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null ? { ...value } : {};
}

/** Copies the bytes to an OWN ArrayBuffer (stable BufferSource for WebCrypto). */
function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const out = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(out).set(bytes);
  return out;
}

async function deriveKey(passphrase: string, salt: Uint8Array, iter: number): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey(
    "raw",
    toArrayBuffer(utf8Encode(passphrase)),
    "PBKDF2",
    false,
    ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", hash: "SHA-256", salt: toArrayBuffer(salt), iterations: iter },
    material,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

/** Serializes and encrypts ALL the local state with the user's passphrase. */
export async function exportEncryptedBackup(
  state: PersistedState,
  passphrase: string,
): Promise<string> {
  if (passphrase.length < 6) {
    throw new Error("backup: passphrase must have at least 6 characters");
  }
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(passphrase, salt, PBKDF2_ITERATIONS);
  const plain = utf8Encode(encode(state));
  const ct = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: toArrayBuffer(iv) },
    key,
    toArrayBuffer(plain),
  );
  const blob: BackupBlob = {
    v: 1,
    kind: BACKUP_KIND,
    kdf: "PBKDF2-SHA256",
    iter: PBKDF2_ITERATIONS,
    salt: bytesToBase64(salt),
    iv: bytesToBase64(iv),
    ct: bytesToBase64(new Uint8Array(ct)),
  };
  return JSON.stringify(blob);
}

/**
 * Decrypts and validates a backup. Throws if the passphrase is wrong, the blob
 * is tampered (GCM does not authenticate) or the shape is not the expected one.
 */
export async function importEncryptedBackup(
  text: string,
  passphrase: string,
): Promise<PersistedState> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error("backup: file is not a Susurro backup");
  }
  const r = asRecord(parsed);
  if (
    r.v !== 1 ||
    r.kind !== BACKUP_KIND ||
    r.kdf !== "PBKDF2-SHA256" ||
    typeof r.iter !== "number" ||
    r.iter < 1 ||
    typeof r.salt !== "string" ||
    typeof r.iv !== "string" ||
    typeof r.ct !== "string"
  ) {
    throw new Error("backup: file is not a Susurro backup");
  }
  const key = await deriveKey(passphrase, base64ToBytes(r.salt), r.iter);
  let plain: ArrayBuffer;
  try {
    plain = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: toArrayBuffer(base64ToBytes(r.iv)) },
      key,
      toArrayBuffer(base64ToBytes(r.ct)),
    );
  } catch {
    throw new Error("backup: incorrect passphrase or corrupted backup");
  }
  const state = decode(utf8Decode(new Uint8Array(plain)));
  const sr = asRecord(state);
  const identity = asRecord(sr.identity);
  if (typeof identity.deviceId !== "string" || !Array.isArray(sr.conversations)) {
    throw new TypeError("backup: unexpected content");
  }
  // Trust boundary: it is our own serialized schema (same as
  // `loadState` with the localStorage blob), already authenticated by GCM.
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
  return state as PersistedState;
}
